/**
 * Hook for managing widget resize functionality
 * Handles mouse-based resize interactions with preview and snapping
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { GridSize } from '../utils/grid-size-utils'
import { calculateColSpan } from '../utils/grid-size-utils'

interface UseWidgetResizeProps {
    widgetId: string
    currentColSpan: GridSize
    isLocked: boolean
    onResize?: (id: string, newColSpan: GridSize) => void
}

interface UseWidgetResizeReturn {
    isResizing: boolean
    previewColSpan: GridSize | null
    resizePosition: { top: number; left: number } | null
    handleResizeStart: (e: React.MouseEvent, cardRef: HTMLDivElement) => void
}

export function useWidgetResize({
    widgetId,
    currentColSpan,
    isLocked,
    onResize,
}: UseWidgetResizeProps): UseWidgetResizeReturn {
    const [isResizing, setIsResizing] = useState(false)
    const [previewColSpan, setPreviewColSpan] = useState<GridSize | null>(null)
    const [resizePosition, setResizePosition] = useState<{
        top: number
        left: number
    } | null>(null)

    const previewColSpanRef = useRef<GridSize | null>(null)
    const resizeStartRef = useRef<{
        x: number
        width: number
        containerWidth: number
    } | null>(null)

    // Keep ref in sync with state
    useEffect(() => {
        previewColSpanRef.current = previewColSpan
    }, [previewColSpan])

    const handleResizeStart = useCallback(
        (e: React.MouseEvent, cardRef: HTMLDivElement) => {
            if (isLocked) return
            e.preventDefault()
            e.stopPropagation()

            const card = cardRef
            if (!card) return

            const container = card.parentElement
            if (!container) return

            // Capture position relative to container
            const cardRect = card.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            setResizePosition({
                top: cardRect.top - containerRect.top,
                left: cardRect.left - containerRect.left,
            })

            setIsResizing(true)
            resizeStartRef.current = {
                x: e.clientX,
                width: card.offsetWidth,
                containerWidth: container.offsetWidth,
            }

            const handleMouseMove = (moveEvent: MouseEvent) => {
                if (!resizeStartRef.current) return

                const deltaX = moveEvent.clientX - resizeStartRef.current.x
                const newColSpan = calculateColSpan(
                    deltaX,
                    resizeStartRef.current.width,
                    resizeStartRef.current.containerWidth,
                    currentColSpan
                )

                setPreviewColSpan(newColSpan)
                previewColSpanRef.current = newColSpan
            }

            const handleMouseUp = () => {
                const finalColSpan = previewColSpanRef.current
                if (finalColSpan && onResize) {
                    onResize(widgetId, finalColSpan)
                }
                setIsResizing(false)
                setPreviewColSpan(null)
                setResizePosition(null)
                previewColSpanRef.current = null
                resizeStartRef.current = null
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        },
        [isLocked, currentColSpan, widgetId, onResize]
    )

    return {
        isResizing,
        previewColSpan,
        resizePosition,
        handleResizeStart,
    }
}
